import {
  createContext,
  useContext,
  useId,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/utils';

interface TabsContextValue {
  value: string;
  onChange: (next: string) => void;
  groupId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabs(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tabs children must be used within <Tabs>');
  return ctx;
}

interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  className?: string;
}

export function Tabs({ value, onValueChange, children, className }: TabsProps) {
  const groupId = useId();
  return (
    <TabsContext.Provider value={{ value, onChange: onValueChange, groupId }}>
      <div className={cn('flex flex-col gap-3', className)}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsList({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      role="tablist"
      className={cn(
        'inline-flex gap-1 rounded-md border border-(--color-border) bg-(--color-surface-elevated) p-1',
        className,
      )}
      {...props}
    />
  );
}

interface TabsTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
}

export function TabsTrigger({ value, className, children, ...props }: TabsTriggerProps) {
  const { value: current, onChange, groupId } = useTabs();
  const selected = current === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      aria-controls={`${groupId}-${value}`}
      id={`${groupId}-tab-${value}`}
      tabIndex={selected ? 0 : -1}
      onClick={() => onChange(value)}
      className={cn(
        'inline-flex h-9 min-w-20 items-center justify-center rounded-sm px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-accent)',
        selected
          ? 'bg-(--color-surface) text-(--color-fg) shadow-sm'
          : 'text-(--color-fg-muted) hover:text-(--color-fg)',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

interface TabsContentProps extends HTMLAttributes<HTMLDivElement> {
  value: string;
}

export function TabsContent({ value, className, ...props }: TabsContentProps) {
  const { value: current, groupId } = useTabs();
  if (current !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${groupId}-${value}`}
      aria-labelledby={`${groupId}-tab-${value}`}
      className={cn('flex flex-col gap-4', className)}
      {...props}
    />
  );
}
